// Il caricamento di un video dall'iPad (richiesta #25), a pezzi.
//
// Un solo caricamento alla volta per tutta l'app, e vive qui, fuori dalle schermate: cambiare
// pagina non lo ferma (solo chiudere il quaderno). Le promesse, in ordine di importanza:
//  1. la voce di mamma passa sempre prima. Finché registra — tutto `#registra` (body.registrando,
//     anche fra un turno e l'altro), o un Recorder vivo da qualunque parte — non parte nessun
//     pezzo; quello in volo si interrompe all'evento `memorie:voce` e si rimanda identico, e si
//     riprende tre secondi dopo che la linea è tornata libera;
//  2. la rete che cade non è un errore: si riprova da soli (2, 4, 8, 15, 30… secondi) finché la
//     pagina vive, e subito appena torna la connessione. Un pezzo si lascia solo quando è FERMO
//     (`fermoS` senza un byte in salita), mai per un tempo fisso: una rete lenta che avanza arriva;
//  3. niente ricarica a metà: `attivo()` dice ad app.ts di rimandare l'aggiornamento dell'app.
// Se il quaderno si chiude il File si perde (copiarlo vorrebbe dire centinaia di MB doppi
// sull'iPad): resta solo un segno in localStorage, e la storia offre di sceglierlo di nuovo.
import { api, ApiError } from './api.js';
import { EVENTO_VOCE, registratoriAttivi } from './recorder.js';

const MIB = 1024 * 1024;
/** Il segno di un caricamento in corso, per riprenderlo dopo un quaderno chiuso. */
export const CHIAVE_IN_CORSO = 'memorie_video_in_corso';
const ATTESE_RETE_S = [2, 4, 8, 15, 30];
/** Senza un byte in salita per tanto, il pezzo è fermo (se il server non dice altro con l'avvio). */
const FERMO_S = 60;
const MARGINE_VOCE_MS = 3000;
const DURATA_ATTESA_MS = 8000;
/** I secondi in più che il server concede (services/video-regole.ts). */
const TOLLERANZA_DURATA_S = 2;

export type Fase =
  | 'guardo' | 'invio' | 'voce' | 'rete' | 'unione'   // in corso
  | 'arrivato' | 'gia' | 'annullato' | 'no';          // finite
export const FASI_FINITE: ReadonlySet<Fase> = new Set(['arrivato', 'gia', 'annullato', 'no']);

export type StatoCaricamento = {
  memoryId: number;
  fase: Fase;
  /** Quanto del file è arrivato, da 0 a 100. */
  percento: number;
  /** La frase per lei, con la voce del quaderno. */
  testo: string;
  mediaId: number | null;
  /** Una fase finita già mostrata in una schermata: la volta dopo non si ripete. */
  vista?: boolean;
};

/** I tetti che il server manda con la storia (`aggiungiVideo`). */
export type Regole = { durataMaxS: number; pesoMaxMb: number; pronomeCaro?: 'lui' | 'lei' };
export type Segno = { cid: string; memoryId: number; bytes: number; impronta: string; at: number };

// ---------- le parole ----------

/** «6 minuti», «un minuto e mezzo», «40 secondi», «più di 3 minuti». */
export function quanto(secondi: number, esatto = false): string {
  const s = Math.round(secondi);
  if (s < 60) return `${s} secondi`;
  const m = Math.floor(s / 60);
  const resto = s % 60;
  const minuti = m === 1 ? 'un minuto' : `${m} minuti`;
  if (resto === 0) return minuti;
  if (resto === 30) return `${minuti} e mezzo`;
  return esatto ? `${minuti} e ${resto} secondi` : `più di ${minuti}`;
}

/** Il tetto, detto come lo direbbe lei: «fino a 3» dopo «dura 6 minuti». */
function finoA(maxS: number): string {
  return maxS % 60 === 0 ? String(maxS / 60) : quanto(maxS, true);
}

export const TESTI = {
  guardo: 'Un attimo, guardo il video…',
  invio: (p: number): string => `Sto portando il video nel quaderno… ${p}%`,
  tieniAperto: 'Tieni aperto il quaderno finché ho finito.',
  rete: 'La connessione si è fermata. Riprovo da sola appena torna.',
  voce: 'Aspetto che finisci di raccontare, poi continuo.',
  unione: 'Sto mettendo insieme il video… un momento.',
  arrivato: 'Il video è arrivato ✓ Adesso lo preparo per guardarlo: ci vuole qualche minuto. Intanto puoi fare altro.',
  arrivatoPrima: 'Il video è arrivato ✓',
  gia: 'Questo video è già nella storia.',
  annullato: 'Va bene, ho lasciato stare.',
  troppoLungo: (durataS: number | null, maxS: number): string =>
    `${durataS !== null ? `Questo video dura ${quanto(durataS)}` : 'Questo video è troppo lungo'}: io posso tenerne fino a ${finoA(maxS)}. `
    + 'Puoi accorciarlo nelle Foto dell’iPad (tocca Modifica) e poi riprovare.',
  troppoPesante: 'Questo video è troppo grande per il quaderno. Prova ad accorciarlo nelle Foto dell’iPad e riprova.',
  oggiBasta: 'Per oggi ho preso abbastanza video. Domani ne prendo altri volentieri.',
  spazio: (caro: string, pronome: 'lui' | 'lei'): string =>
    `Nel quaderno non c’è più posto per altri video. Lo dico a ${caro}: ci pensa ${pronome}.`,
  sostituito: 'Questo video si è fermato perché ne hai scelto un altro. Se lo vuoi ancora, sceglilo di nuovo.',
  nonVideo: 'Questo file non sembra un video. Prova a sceglierlo di nuovo dalle Foto.',
  storiaSparita: 'Questa storia non c’è più: il video non l’ho tenuto.',
  altro: 'Qualcosa non è andato e il video non è arrivato. Riprova tra un po’.',
  ripresa: 'Il video che stavi aggiungendo si è fermato a metà. Sceglilo di nuovo e lo porto nel quaderno.',
  occupato: 'Sto ancora portando un altro video nel quaderno: quando ho finito, puoi aggiungere questo.',
};

// ---------- il segno in localStorage ----------

export function leggiSegno(): Segno | null {
  try {
    const s = JSON.parse(localStorage.getItem(CHIAVE_IN_CORSO) ?? 'null') as Segno | null;
    return s && typeof s.cid === 'string' && Number.isInteger(s.memoryId) ? s : null;
  } catch { return null; }
}
function scriviSegno(s: Segno): void {
  try { localStorage.setItem(CHIAVE_IN_CORSO, JSON.stringify(s)); } catch { /* modalità privata */ }
}
export function togliSegno(cid?: string): void {
  try {
    if (cid && leggiSegno()?.cid !== cid) return;
    localStorage.removeItem(CHIAVE_IN_CORSO);
  } catch { /* ok */ }
}

// ---------- il file ----------

/** L'impronta del file come la calcola il server di prova: SHA-256 di primo MiB + ultimo MiB +
 *  dimensione. Basta a riconoscere lo stesso video scelto due volte senza leggerlo tutto. */
export async function improntaDi(file: Blob): Promise<string> {
  const primo = new Uint8Array(await file.slice(0, MIB).arrayBuffer());
  const ultimo = new Uint8Array(await file.slice(Math.max(0, file.size - MIB)).arrayBuffer());
  const coda = new TextEncoder().encode(String(file.size));
  const tutto = new Uint8Array(primo.length + ultimo.length + coda.length);
  tutto.set(primo, 0);
  tutto.set(ultimo, primo.length);
  tutto.set(coda, primo.length + ultimo.length);
  const h = new Uint8Array(await crypto.subtle.digest('SHA-256', tutto));
  return Array.from(h, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Quanto dura, letto dai metadati. Se non arriva in 8 secondi decide il server. */
export function leggiDurata(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    let finito = false;
    const fine = (d: number | null): void => {
      if (finito) return;
      finito = true;
      window.clearTimeout(timer);
      v.removeAttribute('src');
      v.load();
      URL.revokeObjectURL(url);
      resolve(d);
    };
    const timer = window.setTimeout(() => fine(null), DURATA_ATTESA_MS);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => fine(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    v.onerror = () => fine(null);
    v.src = url;
  });
}

/** La voce di mamma occupa la linea: tutto `#registra`, o un registratore vivo altrove. */
export function voceOccupata(): boolean {
  return document.body.classList.contains('registrando') || registratoriAttivi() > 0;
}

// ---------- la rete ----------

type Risposta = { status: number; body: Record<string, unknown> };

/** Una richiesta JSON che non lancia mai: status 0 = la rete non ha risposto. */
async function chiedi(method: 'POST' | 'DELETE' | 'GET', url: string, corpo?: unknown): Promise<Risposta> {
  try {
    const res = await fetch(url, {
      method,
      headers: corpo !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: corpo !== undefined ? JSON.stringify(corpo) : undefined,
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { status: res.status, body };
  } catch {
    return { status: 0, body: {} };
  }
}

/** La rete che non ha risposto (o un server momentaneamente giù): si riprova. Il 507 no: è lo
 *  spazio finito, una risposta vera, e riprovare non lo cambia. */
const rete = (status: number): boolean => status === 0 || status === 408 || (status >= 500 && status !== 507);

type Lavoro = {
  memoryId: number;
  file: File | null;
  cid: string | null;
  bytes: number;
  pezzoBytes: number;
  pezzi: number;
  /** Quanto può stare fermo un pezzo prima di lasciarlo e rimandarlo. */
  fermoMs: number;
  ricevuti: Set<number>;
  regole: Regole | null;
  caro: string;
  durata: number | null;
  fermato: boolean;
  /** Interrompe il pezzo in volo (la voce, «Annulla»). */
  interrompi: (() => void) | null;
  /** Sveglia un'attesa (la rete tornata, «Annulla»). */
  sveglia: (() => void) | null;
  wakeLock: WakeLockSentinel | null;
};

class Caricatore {
  private lavoro: Lavoro | null = null;
  private stato: StatoCaricamento | null = null;
  private ascoltatori = new Set<(s: StatoCaricamento) => void>();
  private ultimaVoce = 0;

  constructor() {
    if (typeof window === 'undefined') return;
    window.addEventListener(EVENTO_VOCE, () => {
      this.ultimaVoce = Date.now();
      this.lavoro?.interrompi?.();
    });
    window.addEventListener('online', () => this.lavoro?.sveglia?.());
    document.addEventListener('visibilitychange', () => {
      // Lo schermo acceso si perde quando la pagina va in secondo piano: al ritorno si richiede.
      const l = this.lavoro;
      if (l && document.visibilityState === 'visible' && !l.wakeLock) void this.tieniAcceso(l);
    });
  }

  /** C'è un video in viaggio (dalla scelta del file fino alla risposta del server). */
  attivo(): boolean {
    return this.lavoro !== null;
  }

  /** Lo stato per una storia: quello in corso, o l'ultimo finito. */
  statoPer(memoryId: number): StatoCaricamento | null {
    return this.stato?.memoryId === memoryId ? this.stato : null;
  }

  /** Il caricamento in corso è per questo segno (e allora non c'è niente da riprendere). */
  staPortando(cid: string): boolean {
    return this.lavoro?.cid === cid;
  }

  /** Una fase finita è stata mostrata: la prossima schermata non la ripete. */
  segnaVista(): void {
    if (this.stato && FASI_FINITE.has(this.stato.fase)) this.stato.vista = true;
  }
  dimenticaVista(memoryId: number): void {
    if (this.stato?.memoryId === memoryId && this.stato.vista && !this.lavoro) this.stato = null;
  }

  ascolta(fn: (s: StatoCaricamento) => void): () => void {
    this.ascoltatori.add(fn);
    return () => this.ascoltatori.delete(fn);
  }

  private annuncia(): void {
    if (!this.stato) return;
    for (const fn of [...this.ascoltatori]) fn(this.stato);
  }

  private metti(l: Lavoro, fase: Fase, testo: string, percento?: number): void {
    if (this.lavoro !== l || l.fermato) return;
    const p = percento ?? this.stato?.percento ?? 0;
    if (this.stato?.memoryId === l.memoryId && this.stato.fase === fase && this.stato.testo === testo && this.stato.percento === p) return;
    this.stato = { memoryId: l.memoryId, fase, percento: p, testo, mediaId: null };
    this.annuncia();
  }

  /** Fine del viaggio, bene o male: lo stato resta per la storia finché lei non l'ha visto. */
  private concludi(l: Lavoro, fase: Fase, testo: string, mediaId: number | null = null): void {
    if (this.lavoro !== l) return;
    this.lavoro = null;
    void l.wakeLock?.release().catch(() => undefined);
    l.wakeLock = null;
    this.stato = { memoryId: l.memoryId, fase, percento: fase === 'arrivato' || fase === 'gia' ? 100 : this.stato?.percento ?? 0, testo, mediaId };
    this.annuncia();
  }

  private async tieniAcceso(l: Lavoro): Promise<void> {
    if (!('wakeLock' in navigator)) return;
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (this.lavoro !== l) { void lock.release().catch(() => undefined); return; }
      l.wakeLock = lock;
      lock.addEventListener('release', () => { if (l.wakeLock === lock) l.wakeLock = null; });
    } catch { /* facoltativo */ }
  }

  /** Un'attesa che «Annulla» e il ritorno della rete possono accorciare. */
  private dormi(l: Lavoro, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const t = window.setTimeout(() => { l.sveglia = null; resolve(); }, ms);
      l.sveglia = () => { window.clearTimeout(t); l.sveglia = null; resolve(); };
    });
  }

  private async aspettaRete(l: Lavoro, tentativo: number): Promise<void> {
    this.metti(l, 'rete', TESTI.rete);
    await this.dormi(l, ATTESE_RETE_S[Math.min(tentativo, ATTESE_RETE_S.length - 1)]! * 1000);
  }

  /** Finché lei parla non si manda niente; si riparte tre secondi dopo che ha finito. */
  private async aspettaVoce(l: Lavoro): Promise<void> {
    if (voceOccupata()) this.ultimaVoce = Date.now();
    if (Date.now() - this.ultimaVoce >= MARGINE_VOCE_MS) return;
    this.metti(l, 'voce', TESTI.voce);
    while (!l.fermato) {
      if (voceOccupata()) this.ultimaVoce = Date.now();
      else if (Date.now() - this.ultimaVoce >= MARGINE_VOCE_MS) return;
      await new Promise((r) => setTimeout(r, 400));
    }
  }

  private percento(l: Lavoro, inVolo = 0): number {
    let arrivati = inVolo;
    for (const seq of l.ricevuti) arrivati += Math.min(l.pezzoBytes, l.bytes - seq * l.pezzoBytes);
    return l.bytes > 0 ? Math.min(99, Math.floor((arrivati * 100) / l.bytes)) : 0;
  }

  /** Un pezzo, con XMLHttpRequest: la barra segue i byte, e il pezzo si può interrompere. Si
   *  lascia (e si rimanda: per lei «la connessione si è fermata») solo quando è FERMO: nessun
   *  byte in salita, né la risposta dopo l'ultimo, per `fermoMs`. Mai per un tempo fisso: con
   *  due minuti per 4 MiB, sotto i ~280 kbit/s nessun pezzo arrivava mai, e la barra tornava
   *  indietro all'infinito su una rete che funzionava. */
  private mandaPezzo(l: Lavoro, seq: number): Promise<Risposta & { interrotto: boolean }> {
    return new Promise((resolve) => {
      const inizio = seq * l.pezzoBytes;
      const corpo = l.file!.slice(inizio, Math.min(l.bytes, inizio + l.pezzoBytes));
      const xhr = new XMLHttpRequest();
      let interrotto = false;
      let mossoAt = Date.now();
      let mandati = 0;
      // Il guardiano: abort() senza «interrotto» = la rete, e il pezzo si rimanda dopo l'attesa.
      const guardia = window.setInterval(() => {
        if (Date.now() - mossoAt > l.fermoMs) xhr.abort();
      }, Math.max(250, Math.min(5_000, l.fermoMs / 4)));
      l.interrompi = () => { interrotto = true; xhr.abort(); };
      const chiudi = (status: number): void => {
        window.clearInterval(guardia);
        l.interrompi = null;
        let body: Record<string, unknown> = {};
        try { body = JSON.parse(xhr.responseText || '{}') as Record<string, unknown>; } catch { /* niente corpo */ }
        resolve({ status: interrotto ? 0 : status, body, interrotto });
      };
      xhr.open('PUT', `/api/video/caricamenti/${l.cid}/pezzi/${seq}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.loaded > mandati) { mandati = e.loaded; mossoAt = Date.now(); }
        if (interrotto || l.fermato) return;
        const p = this.percento(l, e.loaded);
        if (p !== this.stato?.percento) this.metti(l, 'invio', TESTI.invio(p), p);
      };
      // L'ultimo byte è partito: da qui si aspetta la risposta, con lo stesso margine.
      xhr.upload.onload = () => { mossoAt = Date.now(); };
      xhr.onload = () => chiudi(xhr.status);
      xhr.onerror = () => chiudi(0);
      xhr.onabort = () => chiudi(0);
      xhr.send(corpo);
    });
  }

  /** Il no del server, detto con le sue parole: il caricamento finisce qui. */
  private rifiuto(l: Lavoro, r: Risposta): void {
    const err = String(r.body.error ?? '');
    const pronome = l.regole?.pronomeCaro ?? 'lui';
    const testo =
      err === 'video_troppo_lungo' ? TESTI.troppoLungo(l.durata, Number(r.body.maxS) || l.regole?.durataMaxS || 180)
      : err === 'video_troppo_pesante' ? TESTI.troppoPesante
      : err === 'video_oggi_basta' ? TESTI.oggiBasta
      : err === 'spazio_video_finito' ? TESTI.spazio(l.caro, pronome)
      : err === 'non_e_un_video' || err === 'dati_non_validi' ? TESTI.nonVideo
      : err === 'ricordo_non_trovato' ? TESTI.storiaSparita
      : err === 'caricamento_chiuso' ? (r.body.stato === 'annullato' ? TESTI.annullato : TESTI.sostituito)
      : TESTI.altro;
    if (l.cid) togliSegno(l.cid);
    this.concludi(l, 'no', testo);
  }

  /** «🎞 Aggiungi un video»: comincia il viaggio. Non aspetta la fine. */
  avvia(file: File, memoryId: number, regole: Regole, caro: string): void {
    if (this.lavoro) return;
    const l: Lavoro = {
      memoryId, file, cid: null, bytes: file.size, pezzoBytes: 0, pezzi: 0, fermoMs: FERMO_S * 1000, ricevuti: new Set(),
      regole, caro, durata: null, fermato: false, interrompi: null, sveglia: null, wakeLock: null,
    };
    this.lavoro = l;
    this.stato = null;
    this.metti(l, 'guardo', TESTI.guardo, 0);
    void this.tieniAcceso(l);
    void this.viaggio(l).catch((err: unknown) => {
      console.error(err);
      this.concludi(l, 'no', TESTI.altro);
    });
  }

  /** Il caricamento di prima era arrivato tutto: resta solo da chiuderlo (il server lo sta già
   *  facendo da sé, e la sua `fine` è a volo unico). */
  chiudiArrivato(segno: Segno, caro: string): void {
    if (this.lavoro) return;
    const l: Lavoro = {
      memoryId: segno.memoryId, file: null, cid: segno.cid, bytes: segno.bytes, pezzoBytes: 0, pezzi: 0, fermoMs: FERMO_S * 1000, ricevuti: new Set(),
      regole: null, caro, durata: null, fermato: false, interrompi: null, sveglia: null, wakeLock: null,
    };
    this.lavoro = l;
    this.stato = null;
    this.metti(l, 'unione', TESTI.unione, 100);
    void this.chiusura(l).catch((err: unknown) => {
      console.error(err);
      this.concludi(l, 'no', TESTI.altro);
    });
  }

  /** «Annulla»: il pezzo in volo si interrompe, e quello che era arrivato si butta. */
  async annulla(): Promise<void> {
    const l = this.lavoro;
    if (!l || this.stato?.fase === 'unione') return;
    l.fermato = true;
    l.interrompi?.();
    l.sveglia?.();
    if (l.cid) togliSegno(l.cid);
    this.concludi(l, 'annullato', TESTI.annullato);
    if (l.cid) await chiedi('DELETE', `/api/video/caricamenti/${l.cid}`);
  }

  private async viaggio(l: Lavoro): Promise<void> {
    const file = l.file!;
    const regole = l.regole!;
    // Prima di mandare centinaia di MB: quanto dura e quanto pesa.
    l.durata = await leggiDurata(file);
    if (l.fermato) return;
    if (l.durata !== null && l.durata > regole.durataMaxS + TOLLERANZA_DURATA_S) {
      return this.concludi(l, 'no', TESTI.troppoLungo(l.durata, regole.durataMaxS));
    }
    if (file.size > regole.pesoMaxMb * MIB) return this.concludi(l, 'no', TESTI.troppoPesante);
    const impronta = await improntaDi(file);
    if (l.fermato) return;

    const corpo = {
      nome: file.name || 'video', bytes: file.size, mime: file.type, impronta,
      ...(l.durata !== null ? { durataS: Math.round(l.durata * 10) / 10 } : {}),
    };
    let avvio: Risposta;
    for (let t = 0; ; t++) {
      avvio = await chiedi('POST', `/api/memories/${l.memoryId}/video`, corpo);
      if (l.fermato) {
        // «Annulla» mentre il server apriva il caricamento: non deve restare aperto a contare
        // fra i video di oggi.
        if (avvio.status === 200 && avvio.body.caricamentoId) void chiedi('DELETE', `/api/video/caricamenti/${String(avvio.body.caricamentoId)}`);
        return;
      }
      if (!rete(avvio.status)) break;
      await this.aspettaRete(l, t);
      if (l.fermato) return;
    }
    if (avvio.status !== 200) return this.rifiuto(l, avvio);
    if (avvio.body.giaPresente) return this.concludi(l, 'gia', TESTI.gia, Number(avvio.body.mediaId) || null);

    l.cid = String(avvio.body.caricamentoId);
    l.pezzoBytes = Number(avvio.body.pezzoBytes);
    l.pezzi = Number(avvio.body.pezzi);
    const fermo = Number(avvio.body.fermoS);
    if (Number.isFinite(fermo) && fermo > 0) l.fermoMs = fermo * 1000;
    for (const seq of (avvio.body.ricevuti as number[] | undefined) ?? []) l.ricevuti.add(seq);
    scriviSegno({ cid: l.cid, memoryId: l.memoryId, bytes: file.size, impronta, at: Date.now() });
    // Ripreso: la barra riparte da dove era arrivata.
    const p = this.percento(l);
    this.metti(l, 'invio', TESTI.invio(p), p);
    await this.chiusura(l);
  }

  /** I pezzi che mancano, poi la fine. Se il server dice che ne manca ancora qualcuno, si
   *  rimandano quelli (finché si ha il file) e si riprova. */
  private async chiusura(l: Lavoro): Promise<void> {
    for (;;) {
      if (l.file) {
        const ok = await this.pezzi(l);
        if (!ok || l.fermato) return;
      }
      this.metti(l, 'unione', TESTI.unione, 100);
      let r: Risposta;
      for (let t = 0; ; t++) {
        r = await chiedi('POST', `/api/video/caricamenti/${l.cid}/fine`);
        if (l.fermato) return;
        if (!rete(r.status)) break;
        await this.aspettaRete(l, t);
        if (l.fermato) return;
        this.metti(l, 'unione', TESTI.unione, 100);
      }
      if (r.status === 200) {
        togliSegno(l.cid!);
        const mediaId = Number(r.body.mediaId) || null;
        return r.body.riusato ? this.concludi(l, 'gia', TESTI.gia, mediaId) : this.concludi(l, 'arrivato', TESTI.arrivato, mediaId);
      }
      if (r.status === 409 && r.body.error === 'pezzi_mancanti' && l.file) {
        for (const seq of (r.body.mancanti as number[] | undefined) ?? []) l.ricevuti.delete(seq);
        continue;
      }
      if (r.status === 409 && r.body.error === 'pezzi_mancanti') {
        // Senza il file non si possono rimandare: resta la carta «sceglilo di nuovo».
        return this.concludi(l, 'no', TESTI.ripresa);
      }
      return this.rifiuto(l, r);
    }
  }

  /** Manda i pezzi che il server non ha. Torna false se il caricamento è finito qui. */
  private async pezzi(l: Lavoro): Promise<boolean> {
    let tentativo = 0;
    for (;;) {
      let seq = -1;
      for (let i = 0; i < l.pezzi; i++) if (!l.ricevuti.has(i)) { seq = i; break; }
      if (seq < 0) return true;
      await this.aspettaVoce(l);
      if (l.fermato) return false;
      const p = this.percento(l);
      this.metti(l, 'invio', TESTI.invio(p), p);
      const r = await this.mandaPezzo(l, seq);
      if (l.fermato) return false;
      if (r.interrotto) continue; // la voce: lo stesso pezzo, identico, appena lei ha finito
      if (r.status === 200) {
        tentativo = 0;
        l.ricevuti.add(seq);
        // L'ultimo che mancava: il server sta già chiudendo da sé.
        if (r.body.completo === true) {
          for (let i = 0; i < l.pezzi; i++) l.ricevuti.add(i);
          return true;
        }
        continue;
      }
      if (rete(r.status)) {
        await this.aspettaRete(l, tentativo++);
        continue;
      }
      if (r.status === 507) {
        // Il disco della VPS è quasi pieno: i pezzi arrivati si liberano subito.
        void chiedi('DELETE', `/api/video/caricamenti/${l.cid}`);
      }
      this.rifiuto(l, r);
      return false;
    }
  }
}

export const caricatore = new Caricatore();

export type Ripresa =
  | { tipo: 'arrivato'; segno: Segno; mediaId: number | null }
  | { tipo: 'unione'; segno: Segno }
  | { tipo: 'meta'; segno: Segno };

/** Un caricamento interrotto in questa storia (il quaderno chiuso a metà)? Quello che resta da
 *  dire a lei. I segni che non servono più si tolgono in silenzio. */
export async function daRiprendere(memoryId: number): Promise<Ripresa | null> {
  const segno = leggiSegno();
  if (!segno || segno.memoryId !== memoryId || caricatore.staPortando(segno.cid)) return null;
  let r: { stato: string; completo: boolean; mediaId: number | null };
  try {
    r = await api.get(`/api/video/caricamenti/${segno.cid}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) togliSegno(segno.cid);
    return null;
  }
  if (r.stato === 'fatto') {
    togliSegno(segno.cid);
    return { tipo: 'arrivato', segno, mediaId: r.mediaId };
  }
  if ((r.stato === 'aperto' || r.stato === 'unione') && r.completo) return { tipo: 'unione', segno };
  if (r.stato === 'aperto') return { tipo: 'meta', segno };
  togliSegno(segno.cid);
  return null;
}

/** «Lascia stare»: il caricamento a metà si chiude, e i suoi byte si liberano. */
export async function lasciaStare(segno: Segno): Promise<void> {
  togliSegno(segno.cid);
  await chiedi('DELETE', `/api/video/caricamenti/${segno.cid}`);
}
